
import { defineComponent, PropType, ComponentPublicInstance } from "vue";
import Button from "primevue/button";
import InputSwitch from "primevue/inputswitch";
import Calendar from "primevue/calendar";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import TextArea from "primevue/textarea";
import SplitButton from "primevue/splitbutton";
import useVuelidate from "@vuelidate/core";
import { required, requiredIf, between } from "@vuelidate/validators";

import AttachmentRecords from "@/components/Attachments/AttachmentRecords.vue";

import TicketService from "@/services/TicketService";
import BillingService from "@/services/BillingService";
import CustomerService from "@/services/CustomerService";
import Bill from "../types/bill";
import Ticket from "../types/ticket";
import Part from "@/types/part";
import { mapActions, mapGetters } from "vuex";
import CustResponse from "../types/Responses/custResponse";
import { AttachmentUpload } from "@/types/attachment";
import BillingTypeItem from "@/types/billingTypeItem";

// Skeleton Ticket template for create mode / null ticket prop
const EMPTY_TICKET = (): Ticket => {
  const ticket = new Ticket();
  ticket.id = "";
  ticket.date = new Date().toLocaleDateString('en-CA');
  ticket.cust = "";
  ticket.desc = "";
  ticket.type = "";
  ticket.assigned_to = "";
  ticket.title = "";
  ticket.rate = "";
  ticket.closed = "";
  ticket.part = "";
  ticket.attachments_items = [];
  ticket.project_id = "";
  ticket.project_task = "";
  ticket.status = "";
  ticket.priority = "";
  ticket.conf_notes = "";
  return ticket;
};

export default defineComponent({
  name: "TicketForm",
  components: {
    AttachmentRecords,
    Calendar,
    InputSwitch,
    Button,
    Dropdown,
    InputText,
    TextArea,
    SplitButton,
  },
  emits: ["hide", "onSave", "onStatusUpdate", "onTicketBilling", "onSaveTempAttachment"],
  props: {
    show: Boolean,
    isEditingTicket: {
      type: Boolean,
      default: false,
    },
    showCancelButton: {
      type: Boolean,
      default: true,
    },
    active: {
      type: Number,
      required: true
    },
    ticket: {
      type: Object as PropType<Ticket | null>,
      required: false,
      default: null
    },
    first: {
      type: Number,
      default: 0
    },
    rows: {
      type: Number,
      default: 10
    },
    tempAttachments: {
      type: Array as PropType<AttachmentUpload[]>,
      default: () => []
    }
  },
  created() {
    this.isHidden = this.show;
    this.initFields();
    let currentUserDetails = this.getUser;
    this.currentUser = currentUserDetails.first_name.concat(" "+currentUserDetails.last_name);
  },
  mounted() {
    if (this.isEditingTicket && this.ticket) {
      this.newTicket = JSON.parse(JSON.stringify(this.ticket as Ticket));
      this.selectedTicket = JSON.parse(JSON.stringify(this.ticket as Ticket));
    } else {
      // Initialize for new ticket creation
      this.initFields(); // sets defaults
      this.editTicketField = false; // Enable editing for new tickets
    }
  },
  computed: {
    ...mapGetters({
      getBillingTypes: "billing/getBillingTypeItems",
      getUserIDs: "billing/getUserIdItems",
      getCustTypes: "billing/getCustTypeItems",
      getTickets: "billing/getTickets",
      getUser: "session/getUser",
      getCurrentIndex: "billing/getCurrentIndex",
      getCust: "customer/getCust",
    }),
    oldTicket(): Ticket {
      return this.selectedTicket && Object.keys(this.selectedTicket).length > 0 
        ? this.selectedTicket 
        : (this.ticket || this.getTickets.slice(this.first, this.first + this.rows)[this.active]);
    },
    ticketFieldsHaveChanged(): boolean {
      return Object.keys(this.newTicket as Ticket).some(
        (field: string) =>
          this.newTicket[field as keyof Ticket] !==
          this.selectedTicket[field as keyof Ticket]
      );
    },
    isTypeBillable() {
        const self = this as ComponentPublicInstance & { newTicket: Ticket; getBillingTypes: BillingTypeItem[] };
        const found = (self.getBillingTypes as BillingTypeItem[]).find((element: BillingTypeItem) => self.newTicket.type === element.billing_type);
        return found ? found.billable : false;
    },
    ticketDate: {
      get(): Date {
        return new Date(this.newTicket.date);
      },
      set(value: Date) {
        this.newTicket.date = value.toLocaleDateString('en-CA');
      }
    },
  },
  setup() {
    return {
      v$: useVuelidate(),
    };
  },
  validations() {
    return {
      newTicket: {
        type: { required },
        title: { required },
      },
    };
  },
  data() {
    return {
      isHidden: true,
      editTicketField: true,
      newTicket: {} as Ticket,
      selectedTicket: {} as Ticket,
      newBill: {} as Bill,
      currentUser: "",
      newTicketCust: "",
      submitted: false,
      isLoadingTicket: false,
      parts: [] as Array<Part>,
      part: "" as string | null,
      tempTicketAttachments: [] as AttachmentUpload[],
      saveOptions: [
        {
          label: "Save & Close Ticket",
          command: () => {
            this.saveAndClose();
          },
        },
        {
          label: "",
          command: () => {
            this.updateTicketStatus();
          },
        },
      ],
      ticketService: new TicketService(),
      billService: new BillingService(),
      custService: new CustomerService(),
    };
  },
  methods: {
    ...mapActions({
      addNotification: "notification/add",
      fetchTickets: "billing/getTickets",
      updateTicket: "billing/updateTicket",
      updateTicketRecord: "billing/updateTicketRecord",
      updateCurrentIndex: "billing/updateCurrentIndex",
      addTicketRecord: "billing/addTicketRecord",
      createTicket: "billing/createTicket",
      postAttachment: "attachments/postAttachment"
    }),
    initCust() {
      if (this.newTicket.cust) {
        this.custService
          .fetchCustOptions(
            this.getTickets.slice(this.first, this.first + this.rows)[this.active]?.cust
          )
          .then((response) => {
            if ((response as CustResponse).cust_items.length > 0) {
              const cust = (response as CustResponse).cust_items[0];
              this.newTicketCust = cust.name;
            }
          });
      }
    },
    formatDate(date: string) {
      if (date === null || date === "") return (date = "");
      
      // Check if date is in YYYY-MM-DD format
      const isoDateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
      const match = date.match(isoDateRegex);
      
      if (match) {
        // Date is in YYYY-MM-DD format - reformat directly without parsing
        // This avoids timezone issues when new Date() interprets the string as midnight UTC
        const [_, year, month, day] = match;
        const shortYear = year.substring(2); // Get last 2 digits
        return `${month}-${day}-${shortYear}`;
      }
      
      // For other date formats, parse and format as before
      let stringdate = new Date(date)
        .toLocaleDateString("en-US", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
        })
        .split("/")
        .join("-");
      return stringdate;
    },
    sortArray(x: Part, y: Part) {
      if (x.part_no < y.part_no) {
        return -1;
      }
      if (x.part_no > y.part_no) {
        return 1;
      }
      return 0;
    },
    handleAttachmentSaved(data: { success: boolean; attachmentId: string }) {
      const ref = this.$refs.attachmentRecords as InstanceType<typeof AttachmentRecords>;
      if (data.success && ref) {
        // Update newTicket attachments_items so the record has the latest data
        if (!this.newTicket.attachments_items) {
          this.newTicket.attachments_items = [];
        }
        this.newTicket.attachments_items.push({
          attachments: data.attachmentId,
          attachment_desc: null,
        });
        
        // Also update newBill if it exists (if structure differs)
        if (this.newBill && !this.newBill.attachments_items) {
          this.newBill.attachments_items = [];
        }
        if (this.newBill) {
          this.newBill.attachments_items.push({
            attachments: data.attachmentId,
          });
        }
        
        // Show success notification
        this.addNotification({
          message: 'Successfully uploaded attachment',
          type: 'success',
        });
        
        // Small delay to ensure backend write completes before refetch
        setTimeout(() => {
          ref.fetchAttachmentRecords(false);
        }, 300);
      }
    },
    handleAttachmentDeleted(data: { success: boolean; attachmentId: string }) {
      const ref = this.$refs.attachmentRecords as InstanceType<typeof AttachmentRecords>;
      if (data.success && ref) {
        // Update newTicket attachments_items
        if (this.newTicket.attachments_items) {
          this.newTicket.attachments_items = this.newTicket.attachments_items.filter(
            (item) => item.attachments !== data.attachmentId,
          );
        }
        
        // Also update newBill if it exists
        if (this.newBill && this.newBill.attachments_items) {
          this.newBill.attachments_items = this.newBill.attachments_items.filter(
            (item) => item.attachments !== data.attachmentId,
          );
        }
        
        // Small delay to ensure backend delete completes before refetch
        setTimeout(() => {
          ref.fetchAttachmentRecords(false);
        }, 300);
      }
    },
    handleSaveTempAttachment(data: { tempAttachmentRecords: AttachmentUpload[] }) {
      this.tempTicketAttachments = data.tempAttachmentRecords;
      this.$emit("onSaveTempAttachment", this.tempTicketAttachments);
    },
    resetAttachments() {
      const ref = this.$refs.attachmentRecords as InstanceType<typeof AttachmentRecords>;
      if (ref) {
        ref.resetAttachments();
      }
    },
    async uploadTempAttachments(ticketId: string) {
      // Upload each temporary attachment to the newly created ticket
      const uploadPromises = this.tempTicketAttachments.map((attachment) => {
        return this.postAttachment({
          doc_name: attachment.doc_name,
          doc: attachment.doc,
          mime_type: attachment.mime_type,
          folder: attachment.folder,
          host_file: "TICKET",
          host_id: ticketId,
          user_id: attachment.user_id,
          desc: attachment.desc,
        });
      });

      try {
        await Promise.all(uploadPromises);
        this.addNotification({
          message: `Successfully uploaded ${this.tempTicketAttachments.length} attachment(s)`,
          type: "success",
        });
        this.tempTicketAttachments = []; // Clear temp attachments after upload
      } catch (error) {
        this.addNotification({
          message: `Error uploading some attachments`,
          type: "error",
        });
      }
    },
    async saveTicket() {
      this.submitted = true;
      this.isLoadingTicket = true;

      // Handle creating a new ticket
      if (!this.isEditingTicket) {
        // Validate only the ticket form fields (not attachment dialog fields)
        const isFormValid = await this.v$.newTicket.$validate();
        
        if (!isFormValid) {
          this.addNotification({
            message: 'Please fill in all required fields',
            type: 'error',
          });
          this.isLoadingTicket = false;
          return;
        }

        // Prepare ticket data for creation
        const ticketData = {
          ...this.newTicket,
          cust: this.getCust.cust_id, // Use current customer
          date: this.formatDate(this.newTicket.date?.toString() || new Date().toString()),
        };

        this.createTicket(ticketData)
          .then(async (result: { ticket: Ticket; success: boolean; ticketId?: string }) => {
            // Use ticketId from result or fall back to ticket.id
            const newTicketId = result.ticketId || result.ticket.id;
            
            // Upload temporary attachments if any exist
            if (this.tempTicketAttachments.length > 0 && newTicketId) {
              await this.uploadTempAttachments(newTicketId);
            }
            
            this.isLoadingTicket = false;
            this.submitted = false;

            this.$emit("onSave", {
              ticket: result.ticket,
              success: true,
              refreshTable: true, // Refresh for new tickets
            });
          })
          .catch((error: { ticket: Ticket; success: boolean }) => {
            this.isLoadingTicket = false;
            this.submitted = false;
            
            this.$emit("onSave", {
              ticket: error.ticket,
              success: false,
            });
          });
        return;
      }

      this.newBill.date = new Date().toString();
      this.newBill.hours = "0.0"
      this.newBill.user = this.currentUser;
      this.newBill.date = this.formatDate(this.newBill.date);

        if((this.newBill.desc != undefined || this.newBill.notes != undefined) && !this.ticketFieldsHaveChanged){
          // Commenting on a ticket only (no ticket field changes)
            this.billService
            .postBillingInfo({
              ...this.newBill,
              ticket: this.newTicket.id,
              cust_number: this.newTicket.cust,
            })
            .then(() => {
              this.addNotification({
                message: `Successfully commented on Ticket #${this.newTicket.id}`,
                type: "success",
              });

              this.isLoadingTicket = false;
              this.submitted = false;
              this.newBill = {} as Bill; // reset fields

              this.$emit("onTicketBilling", { success: true });
            })
            .catch(() => {
              this.addNotification({
                message: `Could not comment on Ticket #${this.newTicket.id}`,
                type: "error",
              });
              this.isLoadingTicket = false;
              this.$emit("onTicketBilling", { success: false });
            });
        } else if((this.newBill.desc == undefined && this.newBill.notes == undefined) && this.ticketFieldsHaveChanged) {
            // Updating a ticket only
          const oldTicket = this.oldTicket;

          // Check if the ticket is being closed
          const isClosingTicket = oldTicket.closed !== 'Y' && this.newTicket.closed === 'Y';

          this.ticketService
            .updateTicket({
              newTicket: this.newTicket,
              oldTicket: oldTicket,
              ticket_id: this.newTicket.id,
            })
            .then(() => {
              this.isLoadingTicket = false;

              this.addNotification({
                message: `Successfully updated Ticket #${this.newTicket.id}`,
                type: "success",
              });

              this.updateTicketRecord(
                JSON.parse(JSON.stringify(this.newTicket))
              );
              this.selectedTicket = JSON.parse(
                JSON.stringify(this.newTicket as Ticket)
              );

              this.$emit("onSave", {
                ticket: this.newTicket,
                success: true,
                refreshTable: isClosingTicket, // Refresh when closing a ticket
              });
            })
            .catch(() => {
              this.isLoadingTicket = false;
              this.addNotification({
                message: `Error. Could not update Ticket #${this.newTicket.id}, please try again.`,
                type: "error",
              });
              this.$emit("onSave", {
                ticket: this.newTicket,
                success: true,
                refreshTable: false,
              });
            });
        } else if ((this.newBill.desc != undefined || this.newBill.notes != undefined) && this.ticketFieldsHaveChanged){
          // commenting and updating a ticket
          if (this.newTicket.assigned_to === "UA") {
            this.newTicket.assigned_to = "";
          }
          const oldTicket = this.oldTicket;

          // Check if the ticket is being closed
          const isClosingTicket = oldTicket.closed !== 'Y' && this.newTicket.closed === 'Y';

          this.billService
            .postBillingInfo({
              ...this.newBill,
              ticket: this.newTicket.id,
              cust_number: this.newTicket.cust,
            })
            .then(() => {
              this.$emit("onTicketBilling", { success: true });

              this.addNotification({
                message: `Successfully updated Ticket #${this.newTicket.id}`,
                type: "success",
              });

              this.ticketService
                .updateTicket({
                  newTicket: this.newTicket,
                  oldTicket: oldTicket,
                  ticket_id: this.newTicket.id,
                })
                .then(() => {
                  this.isLoadingTicket = false;

                  this.addNotification({
                    message: `Successfully updated Ticket #${this.newTicket.id}`,
                    type: "success",
                  });

                  this.updateTicketRecord(
                    JSON.parse(JSON.stringify(this.newTicket))
                  );
                  this.selectedTicket = JSON.parse(
                    JSON.stringify(this.newTicket as Ticket)
                  );

                  this.$emit("onSave", {
                    ticket: this.newTicket,
                    success: true,
                    refreshTable: isClosingTicket, // Refresh when closing a ticket
                  });
                })
                .catch(() => {
                  this.isLoadingTicket = false;
                  this.addNotification({
                    message: `Error. Could not update Ticket #${this.newTicket.id}, please try again.`,
                    type: "error",
                  });
                  this.$emit("onSave", {
                    ticket: this.newTicket,
                    success: true,
                    refreshTable: false,
                  });
                });
            })
            .catch(() => {
              this.isLoadingTicket = false;
              this.addNotification({
                message: `Could not comment on Ticket #${this.newTicket.id}`,
                type: "error",
              });
              this.$emit("onTicketBilling", { success: false });
            });
        } else {
          this.isLoadingTicket = false;
        }
    },
    async saveAndClose() {
      this.submitted = true;

      if (this.newTicket.closed === "Y") return;

      this.isLoadingTicket = true;
      this.newBill.date = new Date().toString();
      this.newBill.hours = "0.0";
      this.newBill.user = this.currentUser;
      this.newBill.date = this.formatDate(this.newBill.date);

      if ((this.newBill.desc != undefined || this.newBill.notes != undefined) && !this.ticketFieldsHaveChanged) {
        // Commenting on a ticket only (no ticket field changes)
        this.billService
          .postBillingInfo({
            ...this.newBill,
            ticket: this.newTicket.id,
            cust_number: this.newTicket.cust,
          })
          .then(() => {
            this.addNotification({
              message: `Successfully commented on Ticket #${this.newTicket.id}`,
              type: "success",
            });

            this.isLoadingTicket = false;
            this.submitted = false;
            this.newBill = {} as Bill; // reset fields

            this.$emit("onTicketBilling", { success: true });
            this.postClosedTicket();
          })
          .catch(() => {
            this.addNotification({
              message: `Could not comment on Ticket #${this.newTicket.id}`,
              type: "error",
            });
            this.isLoadingTicket = false;
            this.$emit("onTicketBilling", { success: false });
          });
      } else if((this.newBill.desc == undefined && this.newBill.notes == undefined) && this.ticketFieldsHaveChanged) {
        // Updating and closing a ticket
        const oldTicket = this.oldTicket;

        // Check if the ticket is being closed
        const isClosingTicket = oldTicket.closed !== 'Y' && this.newTicket.closed === 'Y';

        this.ticketService
          .updateTicket({
            newTicket: this.newTicket,
            oldTicket: oldTicket,
            ticket_id: this.newTicket.id,
          })
          .then(() => {
            this.isLoadingTicket = false;

            this.addNotification({
              message: `Successfully updated Ticket #${this.newTicket.id}`,
              type: "success",
            });

            this.updateTicketRecord(
              JSON.parse(JSON.stringify(this.newTicket))
            );
            this.selectedTicket = JSON.parse(
              JSON.stringify(this.newTicket as Ticket)
            );

            this.$emit("onSave", {
              ticket: this.newTicket,
              success: true,
              refreshTable: isClosingTicket, // Refresh when closing a ticket
            });
            this.postClosedTicket();
          })
          .catch(() => {
            this.isLoadingTicket = false;
            this.addNotification({
              message: `Error. Could not update Ticket #${this.newTicket.id}, please try again.`,
              type: "error",
            });
            this.$emit("onSave", {
              ticket: this.newTicket,
              success: true,
              refreshTable: false,
            });
          });
      } else if ((this.newBill.desc != undefined || this.newBill.notes != undefined) && this.ticketFieldsHaveChanged){
        // commenting, updating and closing a ticket
        if (this.newTicket.assigned_to === "UA") {
          this.newTicket.assigned_to = "";
        }
        const oldTicket = this.oldTicket;

        // Check if the ticket is being closed
        const isClosingTicket = oldTicket.closed !== 'Y' && this.newTicket.closed === 'Y';

        this.billService
          .postBillingInfo({
            ...this.newBill,
            ticket: this.newTicket.id,
            cust_number: this.newTicket.cust,
          })
          .then(() => {
            this.$emit("onTicketBilling", { success: true });

            this.addNotification({
              message: `Successfully commented on Ticket #${this.newTicket.id}`,
              type: "success",
            });

            this.ticketService
              .updateTicket({
                newTicket: this.newTicket,
                oldTicket: oldTicket,
                ticket_id: this.newTicket.id,
              })
              .then(() => {
                this.isLoadingTicket = false;

                this.addNotification({
                  message: `Successfully updated Ticket #${this.newTicket.id}`,
                  type: "success",
                });

                this.updateTicketRecord(
                  JSON.parse(JSON.stringify(this.newTicket))
                );
                this.selectedTicket = JSON.parse(
                  JSON.stringify(this.newTicket as Ticket)
                );

                this.$emit("onSave", {
                  ticket: this.newTicket,
                  success: true,
                  refreshTable: isClosingTicket, // Refresh when closing a ticket
                });
                this.postClosedTicket();
              })
              .catch(() => {
                this.isLoadingTicket = false;
                this.addNotification({
                  message: `Error. Could not update Ticket #${this.newTicket.id}, please try again.`,
                  type: "error",
                });
                this.$emit("onSave", {
                  ticket: this.newTicket,
                  success: true,
                  refreshTable: false,
                });
              });
          })
          .catch(() => {
            this.isLoadingTicket = false;
            this.addNotification({
              message: `Could not comment on Ticket #${this.newTicket.id}`,
              type: "error",
            });
            this.$emit("onTicketBilling", { success: false });
          });
      } else {
        this.postClosedTicket();
      }
    },
    updateTicketStatus() {
      const oldTicket = this.getTickets.slice(
        this.first,
        this.first + this.rows
      )[this.active]; // obtain from store

      this.isLoadingTicket = true;

      if (
        this.newTicket.closed == "Y" &&
        this.saveOptions[1].label == "Close"
      ) {
        this.newTicket.closed = "Y";
      } else if (
        (this.newTicket.closed == "N" || this.newTicket.closed == "") &&
        this.saveOptions[1].label == "Open"
      ) {
        this.newTicket.closed = "N";
      } else if (this.newTicket.closed == "N" || this.newTicket.closed == "") {
        this.newTicket.closed = "Y";
      } else {
        this.newTicket.closed = "N";
      }

      const ticketStatus =
        this.newTicket.closed == "N" || this.newTicket.closed == "";

      this.ticketService
        .updateTicket({
          newTicket: this.newTicket,
          oldTicket: oldTicket,
          ticket_id: this.newTicket.id,
        })
        .then(() => {
          this.isLoadingTicket = false;
          this.newBill = {} as Bill;
          this.resetBill();
          this.addNotification({
            message: `Successfully ${
              ticketStatus ? "opened" : "closed"
            } ticket Id ${this.newTicket.id}`,
            type: "success",
          });
          this.$emit("onStatusUpdate", {
            ticket: this.newTicket,
            success: true,
          });
        })
        .catch(() => {
          this.isLoadingTicket = false;
          this.newBill = {} as Bill;
          this.resetBill();
          this.addNotification({
            message: `Failed to ${ticketStatus ? "open" : "close"} ticket Id ${
              this.newTicket.id
            }`,
            type: "error",
          });
          this.$emit("onStatusUpdate", {
            ticket: this.newTicket,
            success: false,
          });
        });
    },
    resetFields() {
      this.newTicket = {} as Ticket;
      this.newBill = {} as Bill;
    },
    resetBill(): void {
      this.newBill = {} as Bill;
      this.newBill.user = this.getUser.user_id;
      this.newBill.date = new Date().toLocaleDateString('en-CA');
    },
    initFields(): void {
      // start from skeleton each time
      this.newTicket = EMPTY_TICKET();
      this.resetBill();

      this.newTicket.assigned_to = this.getUser.user_id;
      this.newTicket.date = new Date().toLocaleDateString('en-CA');

      // Set default type to first option when not editing
      if (!this.isEditingTicket && this.getBillingTypes?.length > 0) {
        this.newTicket.type = this.getBillingTypes[0].billing_type;
      }

      // Add templated text to description for new tickets
      if (!this.isEditingTicket) {
        this.newTicket.desc = `Please reply to this email with the following information if it was not included in your original request:

1. Best phone number to reach you
2. Product name and version
3. Location (if applicable)

Additionally, to the best of your abilities please include this information to help us expedite your request:

1. Name(s) of screens you are having an issue with
2. Any error message(s) you are receiving
3. Steps to reproduce the issue
4. Screenshots
5. For Rover users, please also include the client type (legacy, .NET, web) and the database/account name.`;
      }

      this.newTicketCust = "";
      this.submitted = false;
      this.part = null;
      this.tempTicketAttachments = []; // Clear temp attachments
    },
    postClosedTicket(): Promise<boolean> {
      return new Promise((resolve, reject) => {
        // CLOSE TICKET
        this.newTicket.closed = "";
        const closedNewTicket = JSON.parse(JSON.stringify(this.newTicket));
        closedNewTicket.closed = "Y";
        this.ticketService
          .updateTicket({
            newTicket: closedNewTicket,
            oldTicket: this.newTicket,
            ticket_id: this.newTicket.id,
          })
          .then(() => {
            this.addNotification({
              message: `Successfully updated and closed Ticket #${this.newTicket.id}`,
              type: "success",
            });
            this.isLoadingTicket = false;
            this.submitted = false;
            this.newTicket.closed = "Y";
            this.updateTicketRecord(
              JSON.parse(JSON.stringify(closedNewTicket))
            );
            this.selectedTicket = JSON.parse(JSON.stringify(closedNewTicket));
            resolve(true);
            this.$emit("onStatusUpdate", { success: true });
          })
          .catch((error) => {
            this.addNotification({
              message: `Could not close Ticket #${this.newTicket.id}. ${error.message}.`,
              type: "error",
            });
            this.isLoadingTicket = false;
            reject(false);
            this.$emit("onStatusUpdate", { success: false });
          });
      });
    },
  },  
  watch: {
    show(newShow) {
      this.isHidden = newShow;
      this.initFields();
    },
    getBillingTypes(newTypes) {
      // Set default type when billing types load and we're creating a new ticket
      if (!this.isEditingTicket && newTypes?.length > 0 && !this.newTicket.type) {
        this.newTicket.type = newTypes[0].billing_type;
      }
    },
    active(newActive) {
      if (this.ticket != null) {
        this.newTicket = this.ticket as Ticket;
        this.part = (this.ticket as Ticket).part === "" ? null : (this.ticket as Ticket).part;
        this.selectedTicket = JSON.parse(JSON.stringify(this.ticket as Ticket));

        (this.ticket as Ticket).closed == "" ||
        (this.ticket as Ticket).closed == "N"
          ? (this.saveOptions[1].label = "Close")
          : (this.saveOptions[1].label = "Open");
        
        if (
          this.getCurrentIndex == -1 &&
          this.getTickets.slice(this.first, this.first + this.rows)[
            this.active
          ]?.id == this.newTicket?.id
        ) {
          this.updateCurrentIndex(newActive);
          this.initCust();
        }
      } else {
        // Use skeleton when no ticket provided
        this.initFields();
      }
    },
  },
});
